import React from "react"
import Typography from "aldoo-ra/Typography"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import HCCategoryNote from "./HCCategoryNote"


/**
 * Category content component with grid layout for articles and proper note rendering
 * @param {Object} props
 * @param {Object} props.category - Category data from API
 * @param {Function} props.onNavigate - Navigation handler
 */
const CategoryContent = ({ category, onNavigate }) => {
  const texts = TextBundle({ id: "help-center" })

  if (!category) return null

  // Extract data from the category response
  const title = category.content?.name || "Category"
  const description = category.content?.description || ""
  const articles = category.content?.articles || []
  const subcategories = category.content?.categories || []
  const notes = category.content?.notes || []
  const categoryPath = category.path || ""

  // Filter notes by position
  const topNotes = notes.filter(
    (note) =>
      note.renderas?.startsWith("top_")
  )

  const bottomNotes = notes.filter(
    (note) =>
      note.renderas?.startsWith("bottom_") ||
      !note.renderas
  )

  // Handle article click
  const handleArticleClick = (article) => {
    if (onNavigate) {
      onNavigate("article", article.slug, categoryPath)
    }
  }

  // Handle category click
  const handleCategoryClick = (subcategory) => {
    if (onNavigate) {
      const path = categoryPath
        ? `${categoryPath}/${subcategory.slug}`
        : subcategory.slug

      onNavigate("category", path)
    }
  }

  // Render a card with chevron
  const renderCard = (title, onClick) => (
    <div
      className="h-[116px] flex items-center justify-between gap-6 bg-paper border border-bordered rounded-[30px] cursor-pointer hover:border-secondary hover:shadow-soft_lg transition-shadow p-5 xl:p-[30px]"
      onClick={onClick}
    >
      <Typography variant="body1_medium" className="my-0">
        {title}
      </Typography>

      <div className="w-[30px] h-[30px] flex items-center justify-center flex-shrink-0 bg-primary_lt rounded-full ml-auto">
        <ChevronRightIcon className="w-[18px] h-[18px] text-text stroke-2" />
      </div>
    </div>
  )

  return (
    <div className="w-full lg:w-[620px] xl:w-[752px]">
      <Typography variant="h2" className="mb-[30px]">
        {title}
      </Typography>

      {/* Render top notes using the HCCategoryNote component */}
      {topNotes.map((note) => (
        <HCCategoryNote key={note._id} note={note} />
      ))}

      {description && <div className="text-gray_700 mb-8">{description}</div>}

      {/* Display articles in a grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14">
        {articles.map((article) => (
          <div key={article._id || article.slug}>
            {renderCard(article.label || article.name || article.title, () =>
              handleArticleClick(article)
            )}
          </div>
        ))}

        {subcategories.map((subcategory) => (
          <div key={subcategory._id || subcategory.slug}>
            {renderCard(
              subcategory.label || subcategory.name || subcategory.title,
              () => handleCategoryClick(subcategory)
            )}
          </div>
        ))}
      </div>

      {/* Show empty state if no content */}
      {subcategories.length === 0 && articles.length === 0 && (
        <div className="text-center py-10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 text-gray-300 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>

          <Typography variant="h3" className="text-text">
            {texts?.empty_category_title || "No content found"}
          </Typography>

          <Typography variant="body1_dim">
            {texts?.empty_category_subtitle ||
              "This category does not contain any articles or subcategories."}
          </Typography>
        </div>
      )}

      {/* Render bottom notes */}
      {bottomNotes.map((note) => (
        <HCCategoryNote key={note._id} note={note} />
      ))}
    </div>
  )
}

export default CategoryContent
